import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule, _ } from '@ngx-translate/core';
import { catchError, filter, of, switchMap } from 'rxjs';
import { LoaderComponent } from 'src/app/components/loader/loader.component';
import { TodoListComponent } from 'src/app/components/todo-list/todo-list.component';
import { ValidationStatusComponent } from 'src/app/components/validation-status/validation-status.component';
import { CompanyActivity } from '../../../../models/company';
import { CreateOfferData } from '../../../../models/create-offer/create-offer-data';
import { AppPlatform } from '../../../../models/platform';
import { TodoList } from '../../../../models/todo-list';
import { User } from '../../../../models/user';
import { AuthService } from '../../../../services/auth.service';
import { CategoryService } from '../../../../services/category.service';
import { CompanyService } from '../../../../services/company.service';
import { CategoryUtils } from '../../../../utils/category.utils';

@Component({
    selector: 'app-pick-platform',
    templateUrl: './pick-platform.component.html',
    styleUrls: ['./pick-platform.component.scss'],
    imports: [CommonModule, TranslateModule, RouterModule, LoaderComponent, ValidationStatusComponent, TodoListComponent],
})
export class PickPlatformComponent implements OnInit {
    @Output() nextStep = new EventEmitter<CreateOfferData>();
    @Input() authorized: boolean;
    @Input() todoList: TodoList;
    @Input() readonly = false;
    @Input() isEdit = false;
    @Input('data') inputData?: CreateOfferData;
    user: User;
    loading = true;
    isPro = false;
    data: CreateOfferData = CreateOfferData.createEmpty();

    private activity: CompanyActivity | null = null;
    private catalogReady = false;

    constructor(
        private readonly authService: AuthService,
        private readonly categoryService: CategoryService,
        private readonly companyService: CompanyService,
    ) {}

    ngOnInit() {
        if (this.inputData) {
            this.data = this.inputData;
        }
        this.categoryService.categories.pipe(filter((categories) => !!categories)).subscribe(() => {
            this.catalogReady = true;
        });
        this.authService.userLoggedIn
            .pipe(
                filter((user) => !!user),
                switchMap((user) => {
                    this.user = user;
                    this.isPro = user.pro;
                    if (user.company && (user.pro || user.type === 'team')) {
                        return this.companyService.get(user.company).pipe(catchError(() => of(null)));
                    }
                    return of(null);
                }),
            )
            .subscribe({
                next: (company) => {
                    this.activity = company?.activity ?? null;
                    this.loading = false;
                },
            });
    }

    get selectedPlatform(): AppPlatform | null {
        return this.data?.platformData?.platform ?? null;
    }

    readonly platformCards: { id: AppPlatform; descriptionKey: string; publishKey: string; buttonClass: string }[] = [
        {
            id: 'wholesale',
            descriptionKey: _('Wholesale description'),
            publishKey: _('Publish on wholesale'),
            buttonClass: 'btn-wholesale',
        },
        {
            id: 'marketplace',
            descriptionKey: _('Marketplace description'),
            publishKey: _('Publish on marketplace'),
            buttonClass: 'btn-marketplace',
        },
        { id: 'gazette', descriptionKey: _('Gazette description'), publishKey: _('Publish on gazette'), buttonClass: 'btn-gazette' },
        { id: 'motors', descriptionKey: _('Motors description'), publishKey: _('Publish on motors'), buttonClass: 'btn-motors' },
        { id: 'estate', descriptionKey: _('Estate description'), publishKey: _('Publish on estate'), buttonClass: 'btn-estate' },
    ];

    get isCompanyAccount(): boolean {
        return !!this.user && (this.user.pro || this.user.type === 'team');
    }

    get visiblePlatformCards(): { id: AppPlatform; descriptionKey: string; publishKey: string; buttonClass: string }[] {
        return this.platformCards.filter((card) => this.isPlatformShown(card.id));
    }

    isPlatformEnabled(platform: AppPlatform): boolean {
        return this.allowedPlatforms().includes(platform);
    }

    pickPlatform(platform: AppPlatform) {
        if (this.readonly || !this.isPlatformEnabled(platform)) {
            return;
        }
        this.data.platformData.platform = platform;
        this.nextStep.emit(this.data);
    }

    private isPlatformShown(platform: AppPlatform): boolean {
        if (platform !== 'motors' && platform !== 'estate') {
            return true;
        }

        return this.catalogReady && CategoryUtils.allCategories().some((category) => !!category.isEnabled && category.isPlatform(platform));
    }

    private allowedPlatforms(): AppPlatform[] {
        return CategoryUtils.platformsForPublication(CategoryUtils.allCategories(), this.activity, !this.isCompanyAccount);
    }

    confirmReadonly() {
        this.nextStep.emit(this.data);
    }
}
